import { Badge, Box, Flex, Text } from "@chakra-ui/react";
import CustomerPaginations from "components/pagination/CustomerPaginations";
import AppSpinner from "components/spinner/AppSpinner";
import { SupportChatListItem } from "../../hooks/useMessagesTab";
import { SupportChatsMeta } from "../../hooks/supportApi";

type Props = {
  title: string;
  isLoading: boolean;
  items: SupportChatListItem[];
  meta: SupportChatsMeta | null;
  currentPage: number;
  onChangePage: (page: number) => void;
  selectedChatId: string | number | null;
  onSelect: (chatId: string | number) => void;
};

export default function SupportChatsPanel({
  title,
  isLoading,
  items,
  meta,
  currentPage,
  onChangePage,
  selectedChatId,
  onSelect,
}: Props) {
  const perPage = 6;
  const totalCount = meta?.total ?? 0;
  const totalPages = meta?.lastPage ?? Math.max(1, Math.ceil(totalCount / perPage));
  const showPagination = totalCount > perPage;

  return (
    <Flex
      flex={{ base: "1 1 auto", md: "0 0 308px" }}
      w={{ base: "100%", md: "308px" }}
      minW={{ base: "100%", md: "308px" }}
      maxW={{ base: "100%", md: "308px" }}
      bg={{ base: "transparent", md: "#FFFFFF" }}
      border={{ base: "none", md: "1px solid #E6ECF3" }}
      borderRadius={{ base: "0", md: "12px" }}
      h="100%"
      minH="520px"
      direction="column"
    >
      <Box p="16px">
        <Text fontSize="16px" fontWeight="600" lineHeight="1.3129" color="#0B0E10">
          {title}
        </Text>
      </Box>

      <Box flex="1" overflow="hidden">
        {isLoading && (
          <Flex align="center" justify="center" minH="200px">
            <AppSpinner />
          </Flex>
        )}

        {!isLoading && items.length === 0 && (
          <Text fontSize="14px" color="#6B7280" mt="8px" px="16px">
            You have no support history.
          </Text>
        )}

        {!isLoading && items.length > 0 && (
          <Box overflowY="auto" overflowX="hidden">
            {items.map((chat, index) => {
              const isActive = selectedChatId === chat.id;
              return (
                <Flex
                  key={String(chat.id)}
                  align="flex-start"
                  justify="space-between"
                  gap="16px"
                  h="97px"
                  p="11px 16px"
                  position="relative"
                  cursor="pointer"
                  bg={isActive ? "#F5F9FF" : "#FFFFFF"}
                  border="none"
                  borderTop="1px solid #E6ECF3"
                  onClick={() => onSelect(chat.id)}
                >
                  {isActive ? (
                    <Box
                      position="absolute"
                      left="0"
                      top="0"
                      bottom="0"
                      w="6px"
                      bg="#3A7BFF"
                    />
                  ) : null}
                  <Box>
                    <Flex align="center" gap="8px" mb="8px">
                      <Text
                        fontSize="16px"
                        lineHeight="1.3"
                        fontWeight="600"
                        color="#0B0E10"
                      >
                        {chat.title}
                      </Text>
                      {chat.unreadCount > 0 && (
                        <Badge
                          bgColor="#EF4444"
                          sx={{
                            padding: "2px 8px !important",
                            borderRadius: "999px",
                            fontWeight: "600",
                            fontSize: "12px",
                            lineHeight: "16px",
                            color: "#FFF",
                            minWidth: "20px",
                          }}
                        >
                          {chat.unreadCount}
                        </Badge>
                      )}
                    </Flex>
                    <Text
                      lineHeight="1.3"
                      fontSize="14px"
                      color="#505E70"
                      mb="8px"
                    >
                      #{String(chat.id)}
                    </Text>
                    {chat.displayDate ? (
                      <Text fontSize="14px" lineHeight="1.3" color="#505E70">
                        {chat.displayDate}
                      </Text>
                    ) : null}
                  </Box>

                  <Badge
                    h="32px"
                    w="52px"
                    borderRadius="8px"
                    fontSize="14px"
                    fontWeight="400"
                    bg={chat.isClosed ? "#9AA7B8" : "#B8D0FF"}
                    color={chat.isClosed ? "#FFF" : "#0B0E10"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    textTransform="none"
                  >
                    {chat.isClosed ? "Closed" : "Open"}
                  </Badge>
                </Flex>
              );
            })}
          </Box>
        )}
      </Box>

      {showPagination && (
        <Box pb="16px" mt="auto">
          <CustomerPaginations
            totalCount={totalCount}
            totalPages={totalPages}
            currentPage={currentPage}
            perPage={perPage}
            onChangePage={onChangePage}
            showPageSizeSelector={false}
          />
        </Box>
      )}
    </Flex>
  );
}
